import {
  getTabbableElements,
  useFocusFirstElement,
  useFullscreenElement,
  useModalPortalTarget,
  useStackPagePortalTarget,
} from '@ardoq/hooks';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useToggleHandler } from './useToggleHandler';
import { fontSize, fontWeight, lineHeight } from '@ardoq/design-tokens';
import { POPOVER_WINDOW_EDGE_MARGIN, POPOVER_WRAPPER_PADDING } from './consts';
import { addAnchorName, removeAnchorName } from './anchorNames';

/**
 * `overflow` names the direction the card grows from the anchor. `align`
 * controls what the card's near edge lines up with: `'center'` (default) keeps
 * the near edge offset from the anchor's centre, while `'anchorEdge'` lines it
 * up with the matching anchor edge — its left edge for `overflow: 'right'`, its
 * right edge for `'left'`, and likewise on the block axis for
 * `side: 'left' | 'right'`.
 *
 * `tryOrder` opens the card on whichever side of the anchor has more room,
 * rather than on `side` whenever the card fits there. Use it for content that
 * loads or grows after opening, which may end up fitting on neither side.
 */
export type PopoverAlign = 'center' | 'anchorEdge';
export type PopoverPlacement =
  | {
      side: 'top' | 'bottom';
      overflow: 'left' | 'right' | 'center';
      align?: PopoverAlign;
      tryOrder?: 'most-height';
    }
  | {
      side: 'left' | 'right';
      overflow: 'top' | 'bottom' | 'center';
      align?: PopoverAlign;
      tryOrder?: 'most-width';
    };
export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';
export type PopoverOverflow = 'left' | 'right' | 'top' | 'bottom' | 'center';

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/anchor-name
 */
export type HTMLElementWithAnchor = (HTMLElement | SVGElement) & {
  style: CSSStyleDeclaration & { anchorName?: string };
};

export const anchorNameForId = (id: string): string => `--anchor-${id}`;

/**
 * Checks if an element is visible in the DOM (has non-zero dimensions).
 * This is used to detect if a nested popover's anchor is ready.
 */
const isElementVisible = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/**
 * Waits for an element to become visible using requestAnimationFrame polling.
 * Returns a cleanup function to cancel the waiting.
 *
 * This is used to wait for nested popover anchors to become visible
 * before showing the popover.
 */
const waitForElementVisible = (
  element: Element,
  onVisible: VoidFunction,
  maxAttempts = 60 // ~1 second at 60fps
): VoidFunction => {
  let attempts = 0;
  let rafId: number | null = null;

  const check = () => {
    if (isElementVisible(element)) {
      onVisible();
      return;
    }

    attempts++;
    if (attempts < maxAttempts) {
      rafId = requestAnimationFrame(check);
    }
  };

  rafId = requestAnimationFrame(check);

  return () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
  };
};

const usePopoverPositioning = (
  popoverRef: React.RefObject<HTMLDivElement | null>,
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>,
  isOpen: boolean,
  placement: PopoverPlacement,
  shouldPortal: boolean
) => {
  const [isPositioned, setIsPositioned] = useState(false);

  useEffect(() => {
    const popover = popoverRef.current;
    const anchor = anchorRef.current;
    if (!popover || !anchor) return;

    // The popover <div> may initially be created in the SVG namespace (when
    // the trigger is an SVG child); that element lacks the HTML Popover API.
    // We detect this elsewhere and re-render via a portal — bail out here and
    // let the post-portal re-run call showPopover.
    if (typeof popover.showPopover !== 'function') return;

    let cancelWaitForAnchor: VoidFunction | null = null;
    const isCurrentlyOpen = popover.matches(':popover-open');

    if (isOpen && !isCurrentlyOpen) {
      // Wait for anchor to be visible before showing popover (handles nested popovers)
      const showPopoverWhenReady = () => {
        popover.showPopover();
        setIsPositioned(true);
      };

      if (isElementVisible(anchor)) {
        showPopoverWhenReady();
      } else {
        cancelWaitForAnchor = waitForElementVisible(
          anchor,
          showPopoverWhenReady
        );
      }
    } else if (!isOpen && isCurrentlyOpen) {
      popover.hidePopover();
      setIsPositioned(false);
    } else if (isOpen && isCurrentlyOpen) {
      setIsPositioned(true);
    }

    return () => {
      cancelWaitForAnchor?.();
    };
  }, [popoverRef, anchorRef, isOpen, placement, shouldPortal]);

  return { isPositioned };
};

const useEscPress = (onClose: VoidFunction, isOpen: boolean) => {
  // We store the onClose in a ref to avoid re-registering the listener on every render
  // This ensures multiple popovers can be closed with the same esc click, even if the
  // onClose function is not stable.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCloseRef, isOpen]);
};

const useClickOutside = (
  popoverRef: React.RefObject<HTMLDivElement | null>,
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>,
  isOpen: boolean,
  onClickOutside?: VoidFunction
) => {
  const onClickOutsideRef = useRef(onClickOutside);
  useEffect(() => {
    onClickOutsideRef.current = onClickOutside;
  }, [onClickOutside]);

  useEffect(() => {
    if (!isOpen || !onClickOutsideRef.current) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const popover = popoverRef.current;
      const anchor = anchorRef.current;
      if (!popover) return;

      const target = event.target as Node;

      // If the target is not connected to the DOM, ignore the click
      // This can happen when clicking on an element that is being removed, such as a button inside the popover
      if (!target.isConnected) {
        return;
      }

      // If the click is inside the popover, do nothing
      if (popover.contains(target)) {
        return;
      }

      // If the click is on the anchor element, let the anchor handle it
      if (anchor && anchor.contains(target)) {
        return;
      }

      // Click is outside both popover and anchor
      onClickOutsideRef.current?.();
    };

    // Small delay so that the click event that opened the popover doesn't immediately close it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleDocumentClick);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [popoverRef, anchorRef, isOpen]);
};

const getVisibleTabbableElements = (root: ParentNode): HTMLElement[] =>
  getTabbableElements(root).filter(isElementVisible);

/**
 * Reproduces the native popover Tab sequence for a popover that is opened
 * imperatively (no invoker relationship) and often portaled to <body>: the
 * content behaves as if it were inline right after the trigger, without moving
 * focus on open. We intercept Tab at the four boundaries and route focus to keep
 * the order [trigger] → [popover content] → [element after trigger]:
 *  - Tab from the trigger enters the popover.
 *  - Tab from the last popover element continues after the trigger.
 *  - Shift+Tab from the first popover element returns to the trigger.
 *  - Shift+Tab from the element after the trigger re-enters the popover.
 * The trigger and the element after it live outside the popover, so the listener
 * is on the document.
 */
const useSeamlessTabFocus = (
  popoverRef: React.RefObject<HTMLDivElement | null>,
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>,
  isOpen: boolean
) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      // Read refs on each keydown: the popover node is replaced when
      // useSvgCompatiblePopover switches to the portal path.
      const popover = popoverRef.current;
      const anchor = anchorRef.current;
      if (!popover || !anchor) return;

      const active = document.activeElement;
      const popoverTabbables = getVisibleTabbableElements(popover);
      const firstInPopover = popoverTabbables[0];
      const lastInPopover = popoverTabbables[popoverTabbables.length - 1];

      // The first tabbable that follows the trigger in DOM order. Uses document
      // position rather than the trigger's own index, so it also works when the
      // trigger isn't a native focusable (SVG element, [role=button][tabindex]).
      // Visibility is checked last so getBoundingClientRect only runs for the
      // few following candidates until the first visible one — not the whole doc.
      const afterAnchor = getTabbableElements(document.body).find(
        element =>
          Boolean(
            anchor.compareDocumentPosition(element) &
            Node.DOCUMENT_POSITION_FOLLOWING
          ) &&
          !popover.contains(element) &&
          isElementVisible(element)
      );

      const forward = !event.shiftKey;

      const focusBoundary = (
        target: HTMLElement | SVGElement | null | undefined
      ): void => {
        if (!target) return;
        event.preventDefault();
        target.focus();
      };

      if (forward && active === anchor) {
        focusBoundary(firstInPopover);
      } else if (forward && active === lastInPopover) {
        focusBoundary(afterAnchor);
      } else if (!forward && active === firstInPopover) {
        focusBoundary(anchor);
      } else if (!forward && active === afterAnchor) {
        focusBoundary(lastInPopover);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [popoverRef, anchorRef, isOpen]);
};

const isTransformValue = (value: string): boolean =>
  value !== 'none' && value !== '';

/**
 * Firefox and Safari resolve `anchor-name` against the anchor's untransformed
 * layout box, so a transformed ancestor offsets the popover by exactly that
 * transform (additively, for nested ones). Chrome accounts for it. An AG Grid
 * row is placed with `transform: translateY()`, which is where this bites.
 * @see https://ardoq.atlassian.net/browse/ARD-28699
 */
const hasSelfOrAncestorTransform = (element: Element): boolean => {
  let current: Element | null = element;
  while (current && current !== document.body) {
    const { transform, translate, rotate, scale } = getComputedStyle(current);
    if ([transform, translate, rotate, scale].some(isTransformValue)) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};

/**
 * Get the portal target for rendering the popover. Prefers modal portal target,
 * then stackPage portal target, then fullscreen element, then document.body.
 */
const getPortalTarget = (
  modalPortalTarget: HTMLElement | null,
  stackPagePortalTarget: HTMLElement | null,
  fullscreenElement: Element | null
): HTMLElement => {
  // If we have an explicit modal portal target, use it (e.g., from a modal context)
  if (modalPortalTarget) {
    return modalPortalTarget;
  }

  // Prefer stackPage portal target placed inside FocusScope
  if (stackPagePortalTarget) {
    return stackPagePortalTarget;
  }

  // In fullscreen mode, render to the fullscreen element to keep the popover
  // in the correct stacking context and ensure scroll works properly
  if (
    fullscreenElement instanceof HTMLElement &&
    fullscreenElement !== document.body
  ) {
    return fullscreenElement;
  }

  return document.body;
};

/**
 * Writes `source`'s viewport rect onto `target` every frame until stopped, so a
 * proxy anchor keeps up with an anchor that moves after the popover opened.
 * Neither a `ResizeObserver` nor a scroll listener sees a transform or a
 * reflow that relocates the anchor without resizing it.
 */
const mirrorRect = (source: Element, target: HTMLElement): VoidFunction => {
  let frameId = 0;
  let lastKey = '';

  const tick = () => {
    const { top, left, width, height } = source.getBoundingClientRect();
    const key = `${top}:${left}:${width}:${height}`;
    if (key !== lastKey) {
      lastKey = key;
      target.style.top = `${top}px`;
      target.style.left = `${left}px`;
      target.style.width = `${width}px`;
      target.style.height = `${height}px`;
    }
    frameId = requestAnimationFrame(tick);
  };
  tick();

  return () => cancelAnimationFrame(frameId);
};

/**
 * When the trigger is inside an <svg>, React renders this popover <div> in
 * the SVG namespace — which lacks the HTML Popover API (showPopover). And
 * CSS anchor-positioning via anchor-name on SVG children doesn't give a
 * reliable layout box.
 *
 * Additionally, Firefox and Safari resolve anchor-name against the anchor's
 * untransformed layout box, so a transformed ancestor offsets the popover.
 *
 * This hook handles both cases by using a proxy approach:
 *  - On popover ref-attach, detects the SVG namespace and flips state so
 *    the popover re-renders via createPortal into the correct target (which
 *    could be a modal portal target, fullscreen element, or document.body).
 *  - On anchor ref-attach, detects transformed ancestors.
 *  - When a proxy is needed, mirrors the anchor with an invisible HTML proxy
 *    in the target element, kept on the anchor's rect while the popover is open.
 *  - Otherwise, sets anchor-name directly on the anchor as usual.
 */
const useSvgCompatiblePopover = (
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>,
  id: string,
  isOpen: boolean,
  forcePortal: boolean
) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isSvgNamespace, setIsSvgNamespace] = useState(false);
  const [isInsideTransformedAncestor, setIsInsideTransformedAncestor] =
    useState(false);
  const hasCheckedAnchorTransform = useRef(false);
  const modalPortalTarget = useModalPortalTarget();
  const stackPagePortalTarget = useStackPagePortalTarget();
  const fullscreenElement = useFullscreenElement();
  const portalTarget = getPortalTarget(
    modalPortalTarget,
    stackPagePortalTarget,
    fullscreenElement
  );

  const shouldUseProxy = isSvgNamespace || isInsideTransformedAncestor;
  const shouldPortalPopover = shouldUseProxy || forcePortal;

  const attachPopoverRef = useCallback((element: HTMLDivElement | null) => {
    popoverRef.current = element;
    if (element && typeof element.showPopover !== 'function') {
      setIsSvgNamespace(true);
    }
  }, []);

  // Runs on open rather than on mount, so a grid of closed popovers does not
  // walk each anchor's ancestry. Re-checking on every open is also what catches
  // a transform that appeared after mount: a drag lifting a row, an animation
  // starting.
  const detectTransformedAncestor = useCallback(() => {
    const anchor = anchorRef.current;
    if (!isOpen || hasCheckedAnchorTransform.current || !anchor) return;
    hasCheckedAnchorTransform.current = true;
    setIsInsideTransformedAncestor(hasSelfOrAncestorTransform(anchor));
  }, [anchorRef, isOpen]);

  // The layout pass settles the resulting switch to a portal before
  // `usePopoverPositioning` shows the popover — otherwise the subtree is shown,
  // then unmounted and remounted into the portal. Layout effects run
  // child-before-parent, so a consumer that assigns `anchorRef` from an
  // ancestor's layout effect has no anchor yet at that point; the passive pass
  // catches those, and the ref keeps the ancestry walk to once per open.
  useLayoutEffect(() => {
    detectTransformedAncestor();
    return () => {
      hasCheckedAnchorTransform.current = false;
    };
  }, [detectTransformedAncestor]);

  useEffect(detectTransformedAncestor, [detectTransformedAncestor]);

  useEffect(() => {
    if (!isOpen) return;
    const anchor = anchorRef.current;
    if (!anchor) return;

    const anchorName = anchorNameForId(id);

    if (shouldUseProxy) {
      const proxy: HTMLElementWithAnchor = document.createElement('div');
      proxy.setAttribute('aria-hidden', 'true');
      proxy.style.position = 'fixed';
      proxy.style.pointerEvents = 'none';
      proxy.style.opacity = '0';
      proxy.style.anchorName = anchorName;
      const stopMirroring = mirrorRect(anchor, proxy);
      portalTarget.appendChild(proxy);
      return () => {
        stopMirroring();
        proxy.remove();
      };
    }

    anchor.style.anchorName = addAnchorName(
      anchor.style.anchorName ?? '',
      anchorName
    );
    return () => {
      anchor.style.anchorName = removeAnchorName(
        anchor.style.anchorName ?? '',
        anchorName
      );
    };
  }, [anchorRef, id, shouldUseProxy, isOpen, portalTarget]);

  const renderPopover = useCallback(
    (node: React.ReactElement): React.ReactNode =>
      shouldPortalPopover ? createPortal(node, portalTarget) : node,
    [shouldPortalPopover, portalTarget]
  );

  return {
    popoverRef,
    attachPopoverRef,
    renderPopover,
    shouldPortalPopover,
  };
};

type FunctionalProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  onClickOutside?: VoidFunction;
  id: string;
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>;
};

export type BaseProps = {
  placement: PopoverPlacement;
  /**
   * When true, automatically focuses the first focusable element within the popover content.
   * By default, popovers add content next in the tabbing order without auto-focusing.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Popover_API/Using#popover_accessibility_features
   */
  focusContent?: boolean;
  /**
   * Renders the popover through a portal instead of in place. Needed if a
   * transformed ancestor could sit between the popover and the viewport,
   * which breaks its anchor-positioned layout.
   */
  forcePortal?: boolean;
  className?: string;
  role?: React.AriaRole;
};

export type Popover2025Props = FunctionalProps & BaseProps;

/**
 * Requires the following attributes to be set on the trigger element:
 * - aria-haspopup="dialog"
 * - aria-expanded={isOpen}
 * - aria-controls={id}
 */
export const Popover2025 = ({
  isOpen,
  onClose,
  onClickOutside,
  children,
  id,
  anchorRef,
  placement,
  focusContent,
  forcePortal = false,
  className,
  role,
  ...ariaProps
}: React.PropsWithChildren<Popover2025Props>) => {
  const { popoverRef, attachPopoverRef, renderPopover, shouldPortalPopover } =
    useSvgCompatiblePopover(anchorRef, id, isOpen, forcePortal);

  useEscPress(onClose, isOpen);
  useToggleHandler(popoverRef, isOpen, onClose);
  useClickOutside(popoverRef, anchorRef, isOpen, onClickOutside);
  useSeamlessTabFocus(popoverRef, anchorRef, isOpen);
  const { isPositioned } = usePopoverPositioning(
    popoverRef,
    anchorRef,
    isOpen,
    placement,
    shouldPortalPopover
  );

  useFocusFirstElement({
    containerRef: popoverRef,
    shouldFocus: isPositioned && isOpen && Boolean(focusContent),
  });

  if (!isOpen) return null;

  return renderPopover(
    <StyledPopover
      ref={attachPopoverRef}
      popover="manual" // This attribute is causing warning in the browser console, but is actually valid
      id={id}
      role={role}
      data-react-aria-top-layer="true"
      {...ariaProps}
      $placement={placement}
      $anchorId={id}
      className={className}
    >
      {children}
    </StyledPopover>
  );
};

// For off-centre placements the arrow points at the anchor's centre. Inset the
// popover's near edge `POPOVER_WRAPPER_PADDING + POPOVER_WINDOW_EDGE_MARGIN` past
// that centre. StyledPopoverContainer insets the card from this positioning box
// by its cross-axis window-edge margin, so adding that on top of the content
// padding lands the arrow `POPOVER_WRAPPER_PADDING` from the card's corner,
// regardless of anchor size.
const ARROW_EDGE_INSET = `calc(anchor(center) - ${POPOVER_WRAPPER_PADDING + POPOVER_WINDOW_EDGE_MARGIN}px)`;

// `align: 'anchorEdge'` lines the card's near edge up with the matching anchor
// edge instead of offsetting from its centre. Only the cross-axis window-edge
// margin that StyledPopoverContainer insets the card by has to be cancelled out,
// so the two edges coincide exactly. `position-try-fallbacks` mirror the
// alignment to the opposite edge when the card would leave the viewport.
const INLINE_START_INSET = `calc(anchor(left) - ${POPOVER_WINDOW_EDGE_MARGIN}px)`;
const INLINE_END_INSET = `calc(anchor(right) - ${POPOVER_WINDOW_EDGE_MARGIN}px)`;
const BLOCK_START_INSET = `calc(anchor(top) - ${POPOVER_WINDOW_EDGE_MARGIN}px)`;
const BLOCK_END_INSET = `calc(anchor(bottom) - ${POPOVER_WINDOW_EDGE_MARGIN}px)`;

const anchorPositionStyles: Record<
  PopoverSide,
  Record<PopoverOverflow, string | undefined>
> = {
  top: {
    top: undefined,
    right: `top: auto; right: auto; bottom: anchor(top); left: ${ARROW_EDGE_INSET};`,
    bottom: undefined,
    left: `top: auto; right: ${ARROW_EDGE_INSET}; bottom: anchor(top); left: auto;`,
    center: 'top: auto; bottom: anchor(top); justify-self: anchor-center;',
  },
  right: {
    top: `top: auto; right: auto; bottom: ${ARROW_EDGE_INSET}; left: anchor(right);`,
    right: undefined,
    bottom: `top: ${ARROW_EDGE_INSET}; right: auto; bottom: auto; left: anchor(right);`,
    left: undefined,
    center:
      'top: anchor(center); right: auto; bottom: anchor(center); left: anchor(right); margin-block: auto; height: max-content;',
  },
  bottom: {
    top: undefined,
    right: `top: anchor(bottom); right: auto; bottom: auto; left: ${ARROW_EDGE_INSET};`,
    bottom: undefined,
    left: `top: anchor(bottom); right: ${ARROW_EDGE_INSET}; bottom: auto; left: auto;`,
    center: 'top: anchor(bottom); bottom: auto; justify-self: anchor-center;',
  },
  left: {
    top: `top: auto; right: anchor(left); bottom: ${ARROW_EDGE_INSET}; left: auto;`,
    right: undefined,
    bottom: `top: ${ARROW_EDGE_INSET}; right: anchor(left); bottom: auto; left: auto;`,
    left: undefined,
    center:
      'top: anchor(center); right: anchor(left); bottom: anchor(center); left: auto; margin-block: auto; height: max-content;',
  },
};

// Same side positioning as above, but with the near-edge inset pinned to the
// matching anchor edge (used when `align: 'anchorEdge'`).
const anchorEdgePositionStyles: Record<
  PopoverSide,
  Partial<Record<PopoverOverflow, string>>
> = {
  top: {
    right: `top: auto; right: auto; bottom: anchor(top); left: ${INLINE_START_INSET};`,
    left: `top: auto; right: ${INLINE_END_INSET}; bottom: anchor(top); left: auto;`,
  },
  right: {
    top: `top: auto; right: auto; bottom: ${BLOCK_END_INSET}; left: anchor(right);`,
    bottom: `top: ${BLOCK_START_INSET}; right: auto; bottom: auto; left: anchor(right);`,
  },
  bottom: {
    right: `top: anchor(bottom); right: auto; bottom: auto; left: ${INLINE_START_INSET};`,
    left: `top: anchor(bottom); right: ${INLINE_END_INSET}; bottom: auto; left: auto;`,
  },
  left: {
    top: `top: auto; right: anchor(left); bottom: ${BLOCK_END_INSET}; left: auto;`,
    bottom: `top: ${BLOCK_START_INSET}; right: anchor(left); bottom: auto; left: auto;`,
  },
};

const getAnchorPositionStyle = (
  placement: PopoverPlacement
): string | undefined => {
  const { side, overflow } = placement;
  if (placement.align === 'anchorEdge' && overflow !== 'center') {
    return anchorEdgePositionStyles[side][overflow];
  }
  return anchorPositionStyles[side][overflow];
};

const StyledPopover = styled.div<{
  $placement: PopoverPlacement;
  $anchorId: string;
}>`
  /** Removes default styling */
  margin: 0;
  border: none;
  overflow: visible;
  padding: 0;
  background-color: transparent;
  font-size: ${fontSize.regular};
  font-weight: ${fontWeight.regular};
  line-height: ${lineHeight.regular};
  /* Lets descendants query which position-try fallback is active via
     @container anchored(fallback: ...). */
  /* stylelint-disable-next-line declaration-property-value-no-unknown */
  container-type: anchored;

  ${({ $placement, $anchorId }) => `
    position-anchor: ${anchorNameForId($anchorId)};
    position-try-fallbacks: ${toPositionFallback($placement)};
    position-try-order: ${$placement.tryOrder ?? 'normal'};
    ${getAnchorPositionStyle($placement)}
  `}
`;

const toPositionFallback = (
  placement: PopoverPlacement
): React.CSSProperties['positionTryFallbacks'] => {
  const { side, overflow } = placement;

  if (side === 'top' || side === 'bottom') {
    if (overflow === 'center') {
      return 'flip-block';
    }
    return 'flip-block, flip-inline, flip-block flip-inline';
  }

  if (overflow === 'center') {
    return 'flip-inline';
  }
  return 'flip-inline, flip-block, flip-block flip-inline';
};
